import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import PaymentComponent from 'components/Core/Payment/Payment';
import PaymentAmountInput from 'components/Shared/Inputs/PaymentAmountInput';
import Separator from 'components/Shared/Separator/Separator';
import { colors } from 'styles/cp';
import { Auth } from 'types/Auth';
import { Customizations } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { Insight } from 'types/Insight';
import { Nanopay } from 'types/Nanopay';
import { Sale } from 'types/Sale';
import { Wallet } from 'types/Wallet';

export interface PaymentSectionProps {
  // Payment amount input props
  currency: string;
  balanceAmount: string;
  payment: {
    partialPaymentEnabled?: boolean;
    inputAmount?: string | number;
    defaultInputAmount?: string | number;
    paymentStatus?: string | null;
    isAmountValid?: boolean;
    paymentMethodType?: string;
    allowOverPay?: boolean;
    isDateScheduled?: boolean;
    isAutoPayOn?: boolean;
  };
  featureFlags: FeatureFlags;
  paymentAmountChange: (newAmount: number) => void;
  paymentAmountReset: () => void;

  // Optional props for different contexts
  isEstimate?: boolean;
  estimateAmount?: number;
  region?: string;
  achOnlineConvenienceFeeEnabled?: boolean;
  achOnlineConvenienceFeeAmount?: number;

  // Payment component props (all optional since they may not be available)
  shouldShowPayButton?: boolean;
  customizations?: Customizations;
  config?: Config;
  wallet?: Pick<Wallet, 'enabledPaymentMethods' | 'enabledCCTypes' | 'userWallets'>;
  auth?: Pick<Auth, 'isUserSignedIn'>;
  onPayButtonClicked?: () => void;
  onContinuePbbButtonClick?: () => void;
  recurringInfo?: Record<string, unknown>;
  isSubscription?: boolean;
  insight?: Insight;
  subscriptionInfo?: Record<string, unknown>;
  nanopay?: Pick<Nanopay, 'hasNanopayConnected' | 'payingWithAccount'>;
  isPayEnabled?: boolean;
  paymentMethodsOrder?: string[];
  sale?: Pick<Sale, 'amount' | 'currencyInfo' | 'receivable'>;

  // Styling and layout
  showLabel?: boolean;
  labelText?: string;
  includeSeparator?: boolean;
  separatorHeight?: number;
  showPaymentComponent?: boolean;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
  // Payment amount input props
  currency,
  balanceAmount,
  payment,
  featureFlags,
  paymentAmountChange,
  paymentAmountReset,

  // Optional props
  isEstimate = false,
  estimateAmount,
  region,
  achOnlineConvenienceFeeEnabled,
  achOnlineConvenienceFeeAmount,

  // Styling and layout
  showLabel = true,
  labelText,
  includeSeparator = true,
  separatorHeight = 25,
  showPaymentComponent = true,
}) => {
  const defaultLabelText = labelText || (
    <FormattedMessage id="INVOICE_SUMMARY_MOBILE_PAYMENT_AMOUNT" defaultMessage="Payment amount" />
  );

  return (
    <Fragment>
      <div className="payment-section-wrapper">
        <div className="payment-amount-wrapper">
          {showLabel && <span className="payment-amount-label">{defaultLabelText}</span>}
          <PaymentAmountInput
            paymentAmountChange={paymentAmountChange}
            paymentAmountReset={paymentAmountReset}
            currency={currency}
            balanceAmount={balanceAmount}
            payment={payment}
            featureFlags={featureFlags}
            isEstimate={isEstimate}
            estimateAmount={estimateAmount}
            region={region}
            achOnlineConvenienceFeeEnabled={achOnlineConvenienceFeeEnabled}
            achOnlineConvenienceFeeAmount={achOnlineConvenienceFeeAmount}
          />
        </div>

        {includeSeparator && <Separator height={separatorHeight} />}

        {/* Only render PaymentComponent if explicitly requested */}
        {showPaymentComponent && <PaymentComponent />}
      </div>

      <style jsx>{`
        .payment-section-wrapper {
          padding: 15px 66px;
          max-width: 882px;

          @media screen and (max-width: 768px) {
            padding: 20px 20px 0;
          }
        }

        .payment-amount-wrapper {
          text-align: left;
          margin-top: 10px;
        }

        .payment-amount-label {
          font-family: AvenirNextforINTUIT-Demi;
          font-size: 12px;
          color: ${colors.gray15};
          text-transform: uppercase;
          display: block;
        }
      `}</style>
    </Fragment>
  );
};

export default PaymentSection;
